import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addServicesToState, setLoader } from '../../redux';

import { usePostFile } from '../../hooks/usePostFile';
import { usePostImage } from '../../hooks/usePostImage';
import { useDeleteFile } from '../../hooks/useDeleteFile';
import { useDeleteImage } from '../../hooks/useDeleteImage';

import Lang from './../../selectors/Lang';
import ServiceSelector from '../../selectors/Service';
import NameAndText from '../../components/NameAndText';
import InputImages from '../../components/InputImages';
import InputFileList from '../../components/InputFileList';
import AlertMessage from '../../components/AlertMessage';

import "./style.css"

const init_form = {

    name_fr: '',
    name_en: '',
    name_sp: '',
    name_de: '',

    text_fr: '',
    text_en: '',
    text_sp: '',
    text_de: '',

    images: Array(9).fill({ value: '', prev: null }),
    video: '',

    files: {
        fr: [],
        en: [],
        sp: [],
        de: [],
    },

    parentId: '',
}


function UpdateServices() {
    const dispatch = useDispatch();

    const postFile = usePostFile();
    const postImage = usePostImage();

    const deleteFile = useDeleteFile();
    const deleteImage = useDeleteImage();

    const token = useSelector(state => state.user.token);
    const services_db = useSelector(state => state.data.services);

    const [formData, setFormData] = useState(init_form);
    const [lastData, setLastData] = useState(null);
    const [alertMsg, setAlert] = useState([]);

    const submitForm = async (event) => {
        event.preventDefault();
        if (!formData) return

        if (formData.name_fr === '') {
            setAlert([...alertMsg, {
                title: 'Champ manquant',
                message: 'Le champ "Nom du service" est requis au bon fonctionnement de votre site'
            }]);
            return
        }
        await updateService();
    }

    const updateService = async () => {
        dispatch(setLoader(1));

        let body = {
            name_fr: formData.name_fr,
            name_en: formData.name_en,
            name_sp: formData.name_sp,
            name_de: formData.name_de,

            text_fr: formData.text_fr,
            text_en: formData.text_en,
            text_sp: formData.text_sp,
            text_de: formData.text_de,

            video: formData.video,
            files: {
                fr: [],
                en: [],
                sp: [],
                de: []
            },
        }

        const images = await updateImages();
        body.images = images;

        body.files.fr = await updateFiles('fr');
        body.files.en = await updateFiles('en');
        body.files.sp = await updateFiles('sp');
        body.files.de = await updateFiles('de');

        const req = await fetch(`https://api.pro-vide.eu/admin/service/${formData._id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(body)
        });
        const res = await req.json();

        dispatch(setLoader(-1));

        if ([404, 401, 400].includes(req.status)) setAlert([...alertMsg, {
            title: 'Une erreur c\'est produite lors de la modification du service',
            message: res.message
        }]);

        if (req.status === 500) setAlert([...alertMsg, {
            title: 'Erreur logique, contactez le développeur',
            message: res.message
        }]);

        if (req.status !== 200 || !res) return

        let new_services_db = [];

        // Mise à jour du state 'services'
        for (let service of services_db) {
            if (service._id !== formData._id) {
                new_services_db.push(service);
                continue
            };

            new_services_db.push({
                ...body,
                _id: formData._id,
                name: body.name_fr,
                parentId: formData.parentId
            })
        }

        dispatch(addServicesToState(new_services_db));

        setFormData({ ...init_form });
        setLastData(null);

        setAlert([...alertMsg, {
            title: 'Service modifié',
            message: 'Votre service a bien été modifié'
        }]);
    }

    const updateImages = async () => {
        let response = [];

        for (let i = 0; i < formData.images.length; i++) {
            const image_last = lastData.images[i] || null;
            const image = formData.images[i];

            // Si l'image reste inchangé
            if (image._id) {
                response.push(image._id);
                continue
            }

            // Si il n'y a pas et n'avait pas d'image
            if (!image_last._id && !image._id && image.value === '') continue

            // Si l'ancienne image a été supprimé
            if (image_last._id && !image._id) {
                const image_delete = await deleteImage(image_last._id, setAlert);

                if (!image_delete) {
                    setAlert([...alertMsg, {
                        title: 'Problème d\'image',
                        message: `Un problème est survenue lors de la suppression de la précédente image n° ${i}'`
                    }]);
                    response.push(image_last._id);
                    continue
                }
                if (image.value === '') continue
            }

            // Si une nouvelle image est ajouté
            if (image.value !== '') {
                const formImage = new FormData();
                formImage.append('image', formData.images[i].value);

                // Ajout de l'image à la db
                const image_id = await postImage(setAlert, formImage);

                // Si l'image n'est pas ajouté on continue
                if (!image_id) {
                    setAlert([...alertMsg, {
                        title: 'Problème d\'image',
                        message: `Un problème est survenue lors de la ajout de l'image n° ${i}`
                    }]);
                    continue
                }
                response.push(image_id)
            }
        }
        return response
    }

    const updateFiles = async (lg) => {
        let response = []

        const files = formData.files[lg];
        const last_files = lastData.files[lg];

        // Suppression de tous les fichiers à retirer
        for (let i = 0; i < last_files.length; i++) {
            if (!files.includes(last_files[i])) {
                const file_delete = await deleteFile(last_files[i]._id, setAlert);

                if (!file_delete) {
                    setAlert([...alertMsg, {
                        title: 'Problème de fichier',
                        message: `Un problème est survenue lors de la suppression du précédent fichier n° ${i} version '${lg}'`
                    }]);
                    response.push(last_files[i]._id);
                    continue
                }
            }
        }

        // Ajout des images pré-existante & Ajout de nouveau fichiers
        for (let i = 0; i < files.length; i++) {
            if (files[i]._id) {
                response.push(files[i]._id);
                continue
            }

            const formFile = new FormData();
            formFile.append('file', formData.files[lg][i]);

            const file_id = await postFile(setAlert, formFile);
            response.push(file_id);
        }

        return response
    }

    const reload = () => {
        setFormData(init_form);
        setLastData(null);
    }

    return (
        <div className='modal modal_update_services'>
            {lastData && <Lang />}

            <form onSubmit={submitForm}>
                <h1>Modifier un service</h1>

                {!lastData &&
                    <ServiceSelector
                        formData={formData}
                        setFormData={setFormData}
                        setLastData={setLastData}
                    />
                }

                {lastData &&
                    <section className='sect_name_text_images'>
                        <NameAndText formData={formData} setFormData={setFormData} placeholder='du service' />
                        <InputImages formData={formData} setFormData={setFormData} placeholder='du service' />
                    </section>
                }

                {lastData &&
                    <div className="flyers_video">
                        <InputFileList formData={formData} setFormData={setFormData} />

                        <div className="input_video">
                            <h2>Lien vidéo :</h2>

                            <input
                                type="text"
                                className='inp_video'
                                placeholder='https://www.youtube.com/watch?v=GkZOF9Wk3Zc&t=50s'

                                value={formData.video}
                                onChange={(e) => setFormData({ ...formData, video: e.target.value })}
                            />
                        </div>
                    </div>
                }

                {lastData &&
                    <div className="panel">
                        <button className='btn_return' onClick={() => reload()}>Retour</button>
                        <button className='btn_update'>Modifier</button>
                    </div>
                }
            </form>

            {alertMsg.length !== 0 && <AlertMessage alertMsg={alertMsg} setAlert={setAlert} />}
        </div>
    )
}

export default UpdateServices;